import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Eye, EyeOff, ShoppingBag, Sparkles, Home, UsersRound, type LucideIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { authApi } from '@/services/auth'
import { orgTreeApi } from '@/services/org-tree'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { getErrorMessage } from '@/lib/api'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'

const PHONE_NUMBER_SAMPLE_PLACEHOLDER = '9876543210'

type StartCompanyVertical = 'ecommerce' | 'cleaning' | 'home-services' | 'community'

const VERTICAL_CONFIG: Record<StartCompanyVertical, {
  icon: LucideIcon
  heading: string
  subheading: string
  cardDescription: string
  namePlaceholder: string
  submitLabel: string
}> = {
  ecommerce: {
    icon: ShoppingBag,
    heading: 'Start your online store',
    subheading: 'Create your E-Commerce account in one step.',
    cardDescription: 'Set up your store and admin account — ready to sign in immediately.',
    namePlaceholder: 'Acme Store',
    submitLabel: 'Create store account',
  },
  cleaning: {
    icon: Sparkles,
    heading: 'Start your cleaning company',
    subheading: 'Create your Facility Pro cleaning account in one step.',
    cardDescription: 'Set up your company, team scheduling, and admin account — ready to sign in immediately.',
    namePlaceholder: 'Acme Cleaning',
    submitLabel: 'Create cleaning company account',
  },
  'home-services': {
    icon: Home,
    heading: 'Start your home services company',
    subheading: 'Create your Facility Pro home services account in one step.',
    cardDescription: 'Set up your company, bookings, and admin account — ready to sign in immediately.',
    namePlaceholder: 'Acme Home Services',
    submitLabel: 'Create home services account',
  },
  community: {
    icon: UsersRound,
    heading: 'Start your community',
    subheading: 'Create your Community Pro account in one step.',
    cardDescription: 'Set up your community and admin account — ready to sign in immediately.',
    namePlaceholder: 'Acme Residents Community',
    submitLabel: 'Create community account',
  },
}

const startCompanySignupSchema = z
  .object({
    company_name: z.string().trim().min(1, 'Name is required'),
    owner_name: z.string().trim().min(1, 'Owner name is required'),
    email: z.string().email('Please enter a valid email address'),
    phone_number: z.string().trim().min(1, 'Phone number is required'),
    country_id: z.string().trim().min(1, 'Country is required'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
    password_confirm: z.string(),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: 'Passwords do not match',
    path: ['password_confirm'],
  })

type StartCompanySignupFormData = z.infer<typeof startCompanySignupSchema>

export default function SignupStartCompanyPage() {
  const { vertical } = useParams<{ vertical: string }>()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [businessCode, setBusinessCode] = useState<string | null>(null)
  const [pendingRedirectPath, setPendingRedirectPath] = useState<string | null>(null)

  const config = vertical && vertical in VERTICAL_CONFIG ? VERTICAL_CONFIG[vertical as StartCompanyVertical] : null

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<StartCompanySignupFormData>({
    resolver: zodResolver(startCompanySignupSchema),
    defaultValues: {
      country_id: '94',
    },
  })

  const selectedCountryId = watch('country_id')

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries'],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
    enabled: Boolean(config),
  })

  useEffect(() => {
    if (selectedCountryId || !countriesQuery.data?.length) {
      return
    }

    setValue('country_id', String(countriesQuery.data[0].id), { shouldValidate: false, shouldDirty: false })
  }, [countriesQuery.data, selectedCountryId, setValue])

  if (!config) {
    return <Navigate to="/signup/company" replace />
  }

  const Icon = config.icon

  const onSubmit = async (data: StartCompanySignupFormData) => {
    setIsLoading(true)
    try {
      // No `identifier` here -- the backend generates the Business Code
      // (e.g. ABCDEF), which is what every other admin/worker will need
      // to join this same org afterward.
      const response = await authApi.signup({
        signup_type: 'company',
        company_name: data.company_name,
        facility_product: vertical,
        owner_name: data.owner_name,
        email: data.email,
        phone_number: data.phone_number,
        country_id: Number(data.country_id),
        password: data.password,
      })

      // company_identifier is the human-readable Business Code login actually
      // accepts as `tenant`; service_instance_id is an internal system ID
      // (e.g. "102-923226901378") that looks similar but isn't a valid login
      // reference -- only fall back to it if the Business Code is somehow
      // missing from the response.
      const loginTenant = response.data.company_identifier || response.data.service_instance_id

      if (loginTenant) {
        try {
          const loginResponse = await authApi.login({
            tenant: loginTenant,
            email: data.email,
            password: data.password,
          })

          if (loginResponse.data?.access_token) {
            setPendingRedirectPath(buildPostSelectionPath(loginResponse.data.service_instance_id || loginTenant))
            setBusinessCode(response.data.company_identifier || loginTenant)
            return
          }
        } catch {
          // Signup succeeded; fall through to the sign-in redirect.
        }
      }

      toast.success('Account created successfully! Sign in to continue.')
      navigate(loginTenant ? `/login?tenant=${encodeURIComponent(loginTenant)}` : '/login')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  const continueAfterSignup = () => {
    if (pendingRedirectPath) {
      navigate(pendingRedirectPath, { replace: true })
    }
  }

  if (businessCode) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-primary/5 px-4 py-10">
        <Card className="w-full max-w-md rounded-[28px] border-0 shadow-2xl shadow-slate-950/20">
          <CardHeader className="items-center text-center">
            <CardTitle className="text-xl">Save your business code</CardTitle>
            <CardDescription>
              Every admin and worker joining {config.heading.replace('Start your ', '')} needs this code to sign in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-2xl border border-border/70 bg-primary/5 px-4 py-5 text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary/70">Business code</p>
              <p className="mt-2 text-4xl font-black tracking-[0.15em] text-primary">{businessCode}</p>
            </div>
            <Button type="button" className="w-full" onClick={continueAfterSignup}>
              Continue
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-primary/5 px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-28 bottom-8 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-[40rem] w-[40rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-6xl items-stretch overflow-hidden rounded-[36px] border border-white/70 bg-white/95 shadow-[0_28px_90px_rgba(15,23,42,0.18)] lg:grid-cols-[0.95fr_1.05fr]">
        <section className="relative isolate hidden items-center justify-center overflow-hidden bg-white p-8 lg:flex">
          <div aria-hidden className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div aria-hidden className="absolute -bottom-28 right-8 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
          <div className="relative z-10 flex h-44 w-44 items-center justify-center rounded-[34px] bg-primary/10 text-primary shadow-sm">
            <Icon className="h-20 w-20" />
          </div>
        </section>

        <div className="relative isolate flex h-full w-full flex-col overflow-hidden bg-primary p-6 text-primary-foreground sm:p-8">
        <div aria-hidden className="absolute -right-28 -top-24 h-96 w-96 rounded-full bg-white/20" />
        <div aria-hidden className="absolute -bottom-44 right-8 h-[30rem] w-[30rem] rounded-full bg-slate-900/10" />
        <div aria-hidden className="absolute -right-28 top-1/2 h-[34rem] w-[34rem] -translate-y-1/2 rounded-full border border-white/15" />
        <div className="relative z-10 mb-8 flex flex-col items-center text-center">
          <h1 className="text-2xl font-bold text-white">{config.heading}</h1>
          <p className="mt-1 text-sm text-white/80">{config.subheading}</p>
        </div>

        <Card className="relative z-10 rounded-[28px] border-0 bg-white/95 text-foreground shadow-2xl shadow-slate-950/20 backdrop-blur">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">Company signup</CardTitle>
            <CardDescription>{config.cardDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company name</Label>
                <Input id="company_name" placeholder={config.namePlaceholder} disabled={isLoading} {...register('company_name')} aria-invalid={!!errors.company_name} />
                {errors.company_name ? <p className="text-sm text-destructive">{errors.company_name.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="owner_name">Owner name</Label>
                <Input id="owner_name" placeholder="Jane Doe" disabled={isLoading} {...register('owner_name')} aria-invalid={!!errors.owner_name} />
                {errors.owner_name ? <p className="text-sm text-destructive">{errors.owner_name.message}</p> : null}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country_id">Country</Label>
                  <input type="hidden" {...register('country_id')} />
                  <Select value={selectedCountryId || ''} onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isLoading || countriesQuery.isLoading || !countriesQuery.data?.length}>
                    <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="w-full">
                      <SelectValue placeholder={countriesQuery.isLoading ? 'Loading countries...' : 'Select a country'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(countriesQuery.data ?? []).map((country) => (
                        <SelectItem key={String(country.id)} value={String(country.id)}>
                          {[country.country_name, country.dial_code].filter(Boolean).join(' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {countriesQuery.isError && !errors.country_id ? <p className="text-sm text-destructive">Unable to load countries right now.</p> : null}
                  {errors.country_id ? <p className="text-sm text-destructive">{errors.country_id.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">Phone number</Label>
                  <Input id="phone_number" type="tel" placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER} autoComplete="tel" disabled={isLoading} {...register('phone_number')} aria-invalid={!!errors.phone_number} />
                  {errors.phone_number ? <p className="text-sm text-destructive">{errors.phone_number.message}</p> : null}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="name@example.com" autoComplete="email" disabled={isLoading} {...register('email')} aria-invalid={!!errors.email} />
                {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" autoComplete="new-password" disabled={isLoading} {...register('password')} aria-invalid={!!errors.password} />
                  <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">At least 8 characters with uppercase, lowercase, and a number.</p>
                {errors.password ? <p className="text-sm text-destructive">{errors.password.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirm">Confirm password</Label>
                <div className="relative">
                  <Input id="password_confirm" type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm your password" autoComplete="new-password" disabled={isLoading} {...register('password_confirm')} aria-invalid={!!errors.password_confirm} />
                  <button type="button" onClick={() => setShowConfirmPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password_confirm ? <p className="text-sm text-destructive">{errors.password_confirm.message}</p> : null}
              </div>

              <AuthTermsDisclosure termsText={null} privacyText={null} />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <Spinner className="mr-2" /> : null}
                {config.submitLabel}
              </Button>
            </form>

            <div className="mt-6 rounded-xl border border-border/70 bg-muted/30 p-4">
              <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">Other access options</p>
              <div className="space-y-2 text-center text-sm">
                <div>
                  <span className="text-muted-foreground">Joining an existing organization? </span>
                  <Link to="/signup" className="font-medium text-primary hover:underline">
                    Signup
                  </Link>
                </div>
                <div>
                  <span className="text-muted-foreground">Already have an account? </span>
                  <Link to="/login" className="font-medium text-primary hover:underline">
                    Sign in
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  )
}
